import { type FC, useEffect } from "react";
import { useBoolean } from "usehooks-ts";

import { getUserFullName, getUserInitials } from "../../utils/user-utils";

import { AccountSettingsForm } from "components/forms/AccountSettingsForm";
import { ResetPasswordForm } from "components/forms/ResetPasswordForm";
import { MobileLoginPanel } from "components/header/MobileLoginPanel";
import { onLogout } from "components/materials/sidebar/helpers";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem, MenuLinkItem } from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, CopySendArrowIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentClassUserIsInPersonClass,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectIsStudentPreview,
  selectPresentingSessions,
  userSelectors,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useAv } from "utils/av";
import { Link } from "utils/routing";
import { MONITORING_URL, sectionMonitoringDashboardUrl, useViewLevel } from "utils/urls";
import { WaffleFlags, isMobileLoginEnabled, isWaffleFlagEnabled } from "utils/waffle-flags";

export const WaffleFlagPanel = () => {
  return (
    <div>
      <div className="text-sm font-bold">Flag Status:</div>
      <div />
      {Object.values(WaffleFlags).map((flag) => (
        <div className="flex items-center gap-2 px-2 py-1 italic" key={flag}>
          <div className="text-sm">{flag}</div>
          {isWaffleFlagEnabled(flag) ? (
            <div className="text-sm text-green">ON</div>
          ) : (
            <div className="text-sm text-red">OFF</div>
          )}
        </div>
      ))}
      <div className="mt-2">
        Flags can be modified at{" "}
        <Link className="!text-blue" target="_blank" to="/api/admin/orgs/orgwaffleflag/">
          /api/admin/orgs/orgwaffleflag/
        </Link>
      </div>
    </div>
  );
};

export const ProfileSettingsMenu: FC = () => {
  const dispatch = useAppDispatch();
  const { canAccessMonitoringDashboard, canAuthorSection, canUseBetaFeatures } = useCapabilities();
  const presentingSessions = useAppSelector(selectPresentingSessions);
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentUser = useAppSelector((s) => userSelectors.selectById(s, currentUserId));
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const viewLevel = useViewLevel();
  const { isInVirtualClass } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const shouldShowSectionSettings = canAuthorSection && !viewLevel.home;
  const shouldShowPreviewAsStudent =
    shouldShowSectionSettings && !isInVirtualClass && !isInInPersonClass;
  const shouldShowMobileLogin = canUseBetaFeatures && isMobileLoginEnabled();
  const waffleFlagModalTitle = "Configure Feature Flags";
  useEffect(() => {
    if (currentUserId) {
      storeApi.users.retrieve(currentUserId);
    }
  }, [currentUserId]);

  const {
    value: accountSettingsDialogIsShown,
    setTrue: showAccountSettingsDialog,
    setFalse: hideAccountSettingsDialog,
  } = useBoolean(false);
  const {
    value: mobileLoginDialogIsShown,
    setTrue: showMobileLoginDialog,
    setFalse: hideMobileLoginDialog,
  } = useBoolean(false);
  const {
    value: waffleDialogIsShown,
    setTrue: showWaffleDialog,
    setFalse: hideWaffleDialog,
  } = useBoolean(false);
  const {
    value: resetPasswordShown,
    setTrue: showResetPassword,
    setFalse: hideResetPassword,
  } = useBoolean(false);

  const MenuButton = (
    <SquareButton
      aria-label={t("profile_panel.info_title")}
      eventName="View Account Menu"
      kind="secondary"
      size="s"
      title={t("profile_panel.info_title")}
    >
      <IconText
        className="gap-0.5"
        iconEnd={<ChevronDownIcon />}
        text={getUserInitials(currentUser)}
      />
    </SquareButton>
  );

  return (
    <div className="flex items-center">
      <Menu menuButton={MenuButton} onClick={(e) => e.stopPropagation()}>
        <MenuItem
          aria-label={t("profile_panel.info_title")}
          className="!h-full"
          eventName="Account Settings - Open"
          title={t("profile_panel.info_title")}
          onClick={showAccountSettingsDialog}
        >
          <div className="flex h-full flex-col gap-1">
            <div className="flex flex-col text-sm text-black-tint-55">
              <div>{getUserFullName(currentUser)}</div>
              <div>{currentUser?.email || ""}</div>
            </div>
            <div>{t("profile_panel.info_title")}</div>
          </div>
        </MenuItem>
        {shouldShowSectionSettings && (
          <>
            <MenuDivider />
            <MenuItem
              eventName="Section Settings - Open"
              title={t("settings.section_settings")}
              onClick={() => dispatch(viewStateActions.setSectionSettingsTabIndex(0))}
            >
              {t("settings.section_settings")}
            </MenuItem>
          </>
        )}
        {shouldShowPreviewAsStudent && (
          <MenuItem
            disabled={isStudentPreview}
            eventName="Preview as Student - Start"
            title={t("profile_dropdown.student_preview")}
            onClick={() => dispatch(viewStateActions.setIsStudentPreview(true))}
          >
            {t("profile_dropdown.student_preview")}
          </MenuItem>
        )}
        {canAccessMonitoringDashboard && (
          <MenuLinkItem
            target="_blank"
            to={currentSectionId ? sectionMonitoringDashboardUrl(currentSectionId) : MONITORING_URL}
          >
            {t("main_nav.monitoring_dashboard")}
          </MenuLinkItem>
        )}
        {canUseBetaFeatures && (
          <MenuItem title={waffleFlagModalTitle} onClick={showWaffleDialog}>
            {waffleFlagModalTitle}
          </MenuItem>
        )}
        {shouldShowMobileLogin && (
          <>
            <MenuDivider />
            <MenuItem
              title={t("profile_panel.log_in_with_mobile_device")}
              onClick={showMobileLoginDialog}
            >
              {t("profile_panel.log_in_with_mobile_device")}
            </MenuItem>
          </>
        )}
        <MenuDivider />
        <MenuItem
          eventName="Account settings - Logout"
          title={t("profile_panel.logout")}
          onClick={onLogout(presentingSessions, dispatch)}
        >
          <CopySendArrowIcon />
          {t("profile_panel.logout")}
        </MenuItem>
      </Menu>

      <Dialog
        open={accountSettingsDialogIsShown}
        title={t("profile_panel.info_title")}
        onClose={hideAccountSettingsDialog}
      >
        <AccountSettingsForm
          showResetPassword={showResetPassword}
          onClose={hideAccountSettingsDialog}
        />
      </Dialog>
      <Dialog
        open={mobileLoginDialogIsShown}
        title={t("profile_panel.log_in_with_mobile_device")}
        onClose={hideMobileLoginDialog}
      >
        <MobileLoginPanel />
      </Dialog>
      <Dialog open={waffleDialogIsShown} title={waffleFlagModalTitle} onClose={hideWaffleDialog}>
        <WaffleFlagPanel />
      </Dialog>
      <Dialog
        open={resetPasswordShown}
        title={t("profile_panel.reset_password")}
        onClose={hideResetPassword}
      >
        <ResetPasswordForm onClose={hideResetPassword} />
      </Dialog>
    </div>
  );
};
